/**
 * Inspection Evidence Hook
 * Manages workflow_inspection_evidence CRUD, upload to dvi_media bucket,
 * and accept/reject/place operations with template_item_id → instance row resolution.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logTicketEventToOcis } from '@/utils/ocisIngest';

export interface InspectionEvidence {
  id: string;
  inspection_id: string;
  inspection_item_id: string | null;
  ticket_id: string;
  shop_id: string;
  created_by: string;
  media_type: 'image' | 'video';
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  captured_at: string;
  status: 'unsorted' | 'suggested' | 'confirmed' | 'rejected';
  predicted_template_item_id: string | null;
  predicted_item_key: string | null;
  predicted_item_confidence: number | null;
  suggested_condition: string | null;
  suggested_condition_confidence: number | null;
  suggested_note: string | null;
  classified_at: string | null;
  created_at: string;
  /** Resolved label from template item for display */
  predicted_item_label?: string;
  /** Signed URL for display */
  signedUrl?: string;
}

export function useInspectionEvidence(inspectionId: string | null) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['inspection-evidence', inspectionId],
    queryFn: async (): Promise<InspectionEvidence[]> => {
      if (!inspectionId) return [];
      const { data, error } = await supabase
        .from('workflow_inspection_evidence' as any)
        .select('*')
        .eq('inspection_id', inspectionId)
        .order('captured_at', { ascending: false });
      if (error) throw error;
      const evidence = (data || []) as unknown as InspectionEvidence[];

      // V-E-2 R152-HIGH-1: batched signed-URL generation. The previous
      // pattern (Promise.all + per-evidence createSignedUrl) issued one
      // storage round trip per evidence row. An inspection with many
      // evidence captures (DVI sessions routinely hit 20-50) burned that
      // many sequential storage calls per fetch.
      const paths = evidence.map(e => e.storage_path).filter(Boolean) as string[];
      const signedByPath = new Map<string, string>();
      if (paths.length > 0) {
        const { data: signedList } = await supabase.storage
          .from('dvi_media')
          .createSignedUrls(paths, 3600);
        for (const entry of signedList || []) {
          if (entry.signedUrl && entry.path) {
            signedByPath.set(entry.path, entry.signedUrl);
          }
        }
      }
      const withUrls = evidence.map(e => ({
        ...e,
        signedUrl: signedByPath.get(e.storage_path),
      }));
      return withUrls;
    },
    enabled: !!inspectionId,
  });

  const unsorted = (data || []).filter(e => e.status === 'unsorted');
  const suggested = (data || []).filter(e => e.status === 'suggested');
  const confirmed = (data || []).filter(e => e.status === 'confirmed');
  const rejected = (data || []).filter(e => e.status === 'rejected');

  return { evidence: data || [], unsorted, suggested, confirmed, rejected, isLoading, refetch };
}

export function useUploadInspectionEvidence() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ inspectionId, ticketId, shopId, file }: {
      inspectionId: string;
      ticketId: string;
      shopId: string;
      file: File;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');

      const ext = file.name.split('.').pop() || 'jpg';
      const uuid = crypto.randomUUID();
      const storagePath = `${shopId}/${ticketId}/${inspectionId}/${uuid}.${ext}`;
      const mediaType = file.type.startsWith('video/') ? 'video' : 'image';

      // Upload to storage
      const { error: uploadErr } = await supabase.storage
        .from('dvi_media')
        .upload(storagePath, file, { contentType: file.type, upsert: false });
      if (uploadErr) throw uploadErr;

      // Insert evidence row
      const { data, error: insertErr } = await supabase
        .from('workflow_inspection_evidence' as any)
        .insert({
          inspection_id: inspectionId,
          ticket_id: ticketId,
          shop_id: shopId,
          created_by: user.id,
          media_type: mediaType,
          storage_path: storagePath,
          mime_type: file.type,
          size_bytes: file.size,
          status: 'unsorted',
        } as any)
        .select('id')
        .single();
      if (insertErr) {
        await supabase.storage.from('dvi_media').remove([storagePath]);
        throw insertErr;
      }

      logTicketEventToOcis({
        ticketId,
        shopId,
        eventType: 'inspection_evidence_uploaded' as any,
        content: `Evidence uploaded (${mediaType})`,
        authorUserId: user.id,
      });

      // Fire-and-forget: trigger AI classification
      supabase.functions.invoke('inspect-classify-evidence', {
        body: { evidenceId: (data as any).id, inspectionId, shopId },
      }).catch(err => console.error('[classify] fire-and-forget error:', err));

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-evidence'] });
    },
  });
}

/** Accept an AI suggestion: resolve template_item_id → instance row */
export function useAcceptSuggestion() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ evidenceId, inspectionId, ticketId, shopId }: {
      evidenceId: string;
      inspectionId: string;
      ticketId: string;
      shopId: string;
    }) => {
      // Fetch evidence to get predicted_template_item_id
      const { data: ev } = await supabase
        .from('workflow_inspection_evidence' as any)
        .select('predicted_template_item_id')
        .eq('id', evidenceId)
        .single();
      const templateItemId = (ev as any)?.predicted_template_item_id;
      if (!templateItemId) throw new Error('No predicted template item');

      // Resolve to instance row via template_item_id match
      const { data: instanceItem } = await supabase
        .from('ticket_workflow_inspection_items')
        .select('id')
        .eq('inspection_id', inspectionId)
        .eq('template_item_id', templateItemId)
        .single();
      if (!instanceItem) throw new Error('No matching instance item');

      // Update evidence
      await supabase
        .from('workflow_inspection_evidence' as any)
        .update({ inspection_item_id: instanceItem.id, status: 'confirmed' } as any)
        .eq('id', evidenceId);

      logTicketEventToOcis({
        ticketId,
        shopId,
        eventType: 'inspection_evidence_placed' as any,
        content: 'Evidence placed (AI suggestion accepted)',
        authorUserId: user?.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-evidence'] });
    },
  });
}

/** Reject an AI suggestion */
export function useRejectSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ evidenceId }: { evidenceId: string }) => {
      await supabase
        .from('workflow_inspection_evidence' as any)
        .update({ status: 'rejected', inspection_item_id: null } as any)
        .eq('id', evidenceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-evidence'] });
    },
  });
}

/** Manually place evidence on an item */
export function usePlaceEvidence() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ evidenceId, itemId, ticketId, shopId }: {
      evidenceId: string;
      itemId: string;
      ticketId: string;
      shopId: string;
    }) => {
      await supabase
        .from('workflow_inspection_evidence' as any)
        .update({ inspection_item_id: itemId, status: 'confirmed' } as any)
        .eq('id', evidenceId);

      logTicketEventToOcis({
        ticketId,
        shopId,
        eventType: 'inspection_evidence_placed' as any,
        content: 'Evidence manually placed',
        authorUserId: user?.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-evidence'] });
    },
  });
}
